import React, { useState } from 'react';
import { ChevronRight, Zap, Layers, Target, CheckCircle } from 'lucide-react';
import video1 from '../components/videos/zar-tij.mp4'
import video2 from '../components/videos/reem video.mp4'

const TIJPrintersSection = () => {
  const [activeCategory, setActiveCategory] = useState('handheld');

  const printerTypes = {
    handheld: {
      title: "Handheld TIJ Printers",
      subtitle: "Portable Precision Printing",
      description: "Perfect for on-the-go marking and coding applications with exceptional portability and ease of use.",
      features: ["Lightweight & Portable", "Battery Powered", "Easy Operation", "High Resolution Output"],
      image: "📱", // Using emoji as placeholder
      color: "from-blue-600 to-cyan-500",
      video: video2
    },
    stationary: {
      title: "Multi-Head TIJ Systems",
      subtitle: "Scalable Industrial Solutions",
      description: "Choose from single, dual, or quad-head configurations based on your production line requirements.",
      features: ["1-4 Print Heads Available", "High-Speed Production", "Multi-Line Printing", "Industrial Grade"],
      image: "🖨️", // Using emoji as placeholder
      color: "from-purple-600 to-pink-500",
      video: video1
    }
  };

  const headConfigurations = [
    { heads: 1, lines: "1 Line", description: "Single line printing for basic applications", popular: false },
    { heads: 2, lines: "2 Lines", description: "Dual line printing for enhanced productivity", popular: true },
    { heads: 4, lines: "4 Lines", description: "Maximum throughput for high-volume operations", popular: false }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 py-16 px-4 font-poppins">
      <div className="max-w-7xl mx-auto">
        {/* Header Section */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-blue-100 to-purple-100 rounded-full text-sm font-medium text-slate-700 mb-6 mt-4">
            <Zap className="w-4 h-4 mr-2 text-blue-600" />
            Thermal Inkjet Technology
          </div>
          <h1 className="text-5xl font-bold bg-gradient-to-r from-slate-800 to-slate-600 bg-clip-text text-transparent mb-6">
            Thermal Inkjet Printers
          </h1>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto leading-relaxed font-poppins">
            Revolutionary thermal inkjet printing solutions designed for precision, reliability, and versatility across all your marking and coding applications.
          </p>
        </div>

        {/* Category Toggle */}
        <div className="flex justify-center mb-12">
          <div className="bg-white rounded-2xl p-2 shadow-lg border border-slate-200">
            <button
              onClick={() => setActiveCategory('handheld')}
              className={`px-8 py-3 rounded-xl font-semibold transition-all duration-300 font-poppins ${
                activeCategory === 'handheld'
                  ? 'bg-gradient-to-r from-blue-600 to-cyan-500 text-white shadow-lg'
                  : 'text-slate-600 hover:text-slate-800'
              }`}
            >
              Handheld Printers
            </button>
            <button
              onClick={() => setActiveCategory('stationary')}
              className={`px-8 py-3 rounded-xl font-semibold transition-all duration-300 font-poppins ${
                activeCategory === 'stationary'
                  ? 'bg-gradient-to-r from-purple-600 to-pink-500 text-white shadow-lg'
                  : 'text-slate-600 hover:text-slate-800'
              }`}
            >
              Multi-Head Systems
            </button>
          </div>
        </div>
        

        {/* Main Content */}
        <div className="grid lg:grid-cols-2 gap-12 items-center mb-16">
          {/* Left Side - Info */}
          <div className="space-y-8">
            <div>
              <h2 className={`text-4xl font-bold bg-gradient-to-r ${printerTypes[activeCategory].color} bg-clip-text text-transparent mb-4`}>
                {printerTypes[activeCategory].title}
              </h2>
              <h3 className="text-2xl font-semibold text-slate-700 mb-6 font-poppins">
                {printerTypes[activeCategory].subtitle}
              </h3>
              <p className="text-lg text-slate-600 leading-relaxed mb-8 font-poppins">
                {printerTypes[activeCategory].description}
              </p>
            </div>

            <div className="grid grid-cols-2 gap-4 font-poppins">
              {printerTypes[activeCategory].features.map((feature, index) => (
                <div key={index} className="flex items-center space-x-3 p-4 bg-white rounded-xl border border-slate-200 hover:shadow-md transition-shadow font-poppins">
                  <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                  <span className="font-medium text-slate-700 font-poppins">{feature}</span>
                </div>
              ))}
            </div>

            <button className={`inline-flex items-center px-8 py-4 bg-gradient-to-r ${printerTypes[activeCategory].color} text-white font-semibold rounded-2xl hover:shadow-xl transform hover:-translate-y-1 transition-all duration-300`}>
              Explore Models
              <ChevronRight className="w-5 h-5 ml-2" />
            </button>
          </div>

          {/* Right Side - Visual */}
          <div className="relative">
            <div>
              {/* className={`bg-gradient-to-br ${printerTypes[activeCategory].color} rounded-3xl p-12 shadow-2xl`} */}
              <div className="text-center">
                <div className="text-8xl mb-6 filter drop-shadow-lg">
                  <video 
                    src={printerTypes[activeCategory].video}  
                    autoPlay 
                    loop 
                    muted 
                    playsInline 
                    className="w-full h-auto rounded-2xl"
                  />
                </div>
                <div className="text-white/90 text-lg font-medium">
                  {activeCategory === 'handheld' ? 'Portable Solution' : 'Industrial Strength'}
                </div>
              </div>
            </div>

            {/* Floating Elements */}
            {/* <div className="absolute -top-6 -right-6 bg-white rounded-2xl p-4 shadow-lg border border-slate-200">
              <Target className="w-8 h-8 text-blue-500" />
            </div> */}
            {/* <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl p-4 shadow-lg border border-slate-200">
              <Layers className="w-8 h-8 text-purple-500" />
            </div> */}
          </div>
        </div>

        {/* Multi-Head Configuration Section */}
        {activeCategory === 'stationary' && (
          <div className="bg-white rounded-3xl p-8 shadow-xl border border-slate-200">
            <div className="text-center mb-10">
              <h3 className="text-3xl font-bold text-slate-800 mb-4">Choose Your Configuration</h3>
              <p className="text-lg text-slate-600 font-poppins">Select the perfect number of print heads for your production needs</p>
            </div>

            <div className="grid md:grid-cols-3 gap-6">
              {headConfigurations.map((config, index) => (
                <div key={index} className={`relative p-6 rounded-2xl border-2 transition-all duration-300 hover:shadow-lg font-poppins ${
                  config.popular 
                    ? 'border-purple-500 bg-gradient-to-br from-purple-50 to-pink-50' 
                    : 'border-slate-200 bg-white hover:border-slate-300'
                }`}>
                  {config.popular && (
                    <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                      <span className="bg-gradient-to-r from-purple-600 to-pink-500 text-white px-4 py-1 rounded-full text-sm font-semibold">
                        Most Popular
                      </span>
                    </div>
                  )}
                  
                  <div className="text-center">
                    <div className={`text-4xl font-bold mb-2 ${
                      config.popular ? 'text-purple-600' : 'text-slate-700'
                    }`}>
                      {config.heads}
                    </div>
                    <div className="text-sm text-slate-500 mb-2">Print Head{config.heads > 1 ? 's' : ''}</div>
                    <div className={`text-xl font-semibold mb-3 ${
                      config.popular ? 'text-purple-700' : 'text-slate-800'
                    }`}>
                      {config.lines}
                    </div>
                    <p className="text-slate-600 text-sm leading-relaxed">
                      {config.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* CTA Section */}
        <div className="text-center mt-16 p-12 bg-gradient-to-r from-slate-800 to-slate-700 rounded-3xl shadow-2xl">
          <h3 className="text-3xl font-bold text-white mb-6">Ready to Transform Your Printing Process?</h3>
          <p className="text-xl text-slate-300 mb-8 max-w-2xl mx-auto">
            Discover how MeenJet's thermal inkjet technology can enhance your production efficiency and print quality.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button className="px-8 py-4 bg-white text-slate-800 font-semibold rounded-2xl hover:shadow-xl transform hover:-translate-y-1 transition-all duration-300">
              Request Demo
            </button>
            <button className="px-8 py-4 border-2 border-white text-white font-semibold rounded-2xl hover:bg-white hover:text-slate-800 transition-all duration-300">
              Download Catalog
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TIJPrintersSection;